import axios from "axios";

// Create axios instance with default config
const instance = axios.create({
  withCredentials: true,
  baseURL: "/",
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add a request interceptor
instance.interceptors.request.use(
  (config) => {
    // All API requests (including login) go directly to backend-running server

    // In production and staging, use the proper API domain
    // In development, still use localhost
    if (typeof window !== 'undefined') {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocalhost) {
        // Local development - use local ports
        config.baseURL = process.env.NODE_ENV === 'production'
          ? 'http://localhost:8086'
          : 'http://localhost:8085';
      } else {
        // Remote environment - use proper domains
        config.baseURL = process.env.NODE_ENV === 'production'
          ? 'https://api-v1.yatabase.com'
          : 'https://dev-api.yatabase.com';
      }
    }

    // CRITICAL FIX: Get the access token fresh from storage just before sending
    // This ensures that if TokenRefreshManager updated the token, we use the new one
    let token = sessionStorage.getItem("complexappToken");
    if (!token) {
      token = localStorage.getItem("complexappToken");
    }

    if (token) {
      // Add token to request body
      if (!config.data) {
        config.data = {};
      }
      config.data.token = token;

      // Debug logging to track token usage
      if (process.env.NODE_ENV === 'development') {
        const tokenData = parseTokenExp(token);
        const timeLeft = tokenData ? Math.floor((tokenData.exp * 1000 - Date.now()) / 60000) : 'invalid';
        console.log(`🌐 API Request: ${config.url}, Token expires in: ${timeLeft} min`);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper function for development debugging
function parseTokenExp(token) {
  try {
    if (!token || !token.includes('.')) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return { exp: payload.exp };
  } catch (e) {
    return null;
  }
}

// Track refresh attempts to prevent infinite loops
let refreshAttemptCount = 0;
let lastRefreshAttempt = 0;
const MAX_REFRESH_ATTEMPTS = 3;
const REFRESH_COOLDOWN = 5000; // 5 seconds between refresh attempts

// Add a response interceptor
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If the error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log("🚫 Access token expired (401), attempting to refresh...");
      originalRequest._retry = true;

      // Check if we've exceeded refresh attempts
      const now = Date.now();
      if (now - lastRefreshAttempt < REFRESH_COOLDOWN) {
        refreshAttemptCount++;
        if (refreshAttemptCount >= MAX_REFRESH_ATTEMPTS) {
          console.log("🚫 Too many refresh attempts, logging out");
          const { logout } = await import('./logout');
          await logout();
          refreshAttemptCount = 0;
          return Promise.reject(error);
        }
      } else {
        refreshAttemptCount = 1;
      }
      lastRefreshAttempt = now;

      try {
        // Get the most current token from storage (in case TokenRefreshManager updated it)
        const currentToken = sessionStorage.getItem("complexappToken") || localStorage.getItem("complexappToken");

        // If we don't have any token, logout immediately
        if (!currentToken) {
          console.log("🚫 No token found, logging out");
          const { logout } = await import('./logout');
          await logout();
          return Promise.reject(error);
        }

        // Check token expiry before attempting refresh
        const tokenData = parseTokenExp(currentToken);
        if (tokenData) {
          const timeLeft = Math.floor((tokenData.exp * 1000 - Date.now()) / 1000);
          console.log(`🔍 Current token expires in ${timeLeft} seconds`);

          // If token has more than 1 minute left, this might be a race condition
          if (timeLeft > 60) {
            console.log("🔄 Token still valid, retrying with fresh token from storage");
            // Update the token in the original request with the fresh one
            if (!originalRequest.data) {
              originalRequest.data = {};
            }
            originalRequest.data.token = currentToken;

            // Reset refresh attempt counter on successful retry
            refreshAttemptCount = 0;
            return instance(originalRequest);
          }
        }

        // First, attempt backend refresh (for traditional JWT users)
        const response = await instance.post("/refresh-token", {
          token: currentToken
        }, {
          withCredentials: true
        });

        // Check if this is a Cognito user who needs client-side refresh
        if (response.data.cognitoRefreshRequired) {
          console.log("🔄 Cognito token refresh required - handling client-side");

          // Import Cognito utilities
          const { refreshCognitoToken } = await import('./cognitoAuth');

          try {
            const newToken = await refreshCognitoToken();
            if (newToken) {
              console.log("✅ Successfully refreshed Cognito token via axios interceptor");

              // Update the token in the original request
              if (!originalRequest.data) {
                originalRequest.data = {};
              }
              originalRequest.data.token = newToken;

              // Retry the original request
              return instance(originalRequest);
            }
          } catch (cognitoError) {
            console.error("❌ Failed to refresh Cognito token:", cognitoError);
          }

          // If Cognito refresh failed, logout
          console.log("🚫 Cognito token refresh failed, logging out");
          const { logout } = await import('./logout');
          await logout();
          return Promise.reject(error);
        }

        // Traditional JWT refresh succeeded
        if (response.data.token) {
          console.log("✅ Successfully obtained new access token via backend refresh");

          // Update the token in both storages for persistence
          sessionStorage.setItem("complexappToken", response.data.token);
          localStorage.setItem("complexappToken", response.data.token);

          // Update the token in the original request
          if (!originalRequest.data) {
            originalRequest.data = {};
          }
          originalRequest.data.token = response.data.token;

          // Retry the original request
          return instance(originalRequest);
        }
      } catch (refreshError) {
        console.log("❌ Failed to refresh token:", refreshError);
        // Use unified logout function
        const { logout } = await import('./logout');
        await logout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default instance; 